import { useId, useState, type ReactElement } from "react";
import { ListChecks, ShieldCheck, X } from "lucide-react";
import { Badge, Button } from "../atoms/index.js";
import { ConfirmDialog } from "../molecules/index.js";
import { ModalShell } from "../primitives/index.js";
import { cn } from "../lib/utils.js";
import {
  ProposalReviewForm,
  ProposalReviewFormFooter,
} from "./ProposalReviewForm.js";
import { ProposalReviewItemList } from "./ProposalReviewItemList.js";
import { ProposalApprovalConfirmation } from "./ProposalApprovalConfirmation.js";
import type { ProposalReviewDialogProps } from "./proposal-review.types.js";

/**
 * The large review-and-edit dialog for a proposed external write.
 *
 * ## Why this composes `ModalShell` rather than wrapping `Modal`
 *
 * `Modal` is the opinionated dialog — title bar, one column, `lg` capped at
 * 760px. This surface needs a two-column body at roughly 1200px, a header
 * carrying badges and an unsaved marker, and a sticky footer. That is "fully
 * custom dialog chrome", which this package's own guidance routes to
 * `ModalShell` (portal, scrim, focus trap with return-focus, Esc, scroll lock,
 * inert background) rather than to fighting `Modal`'s defaults.
 *
 * The discard confirmation is the shared `ConfirmDialog` (a `Modal`) — a
 * small, ordinary destructive confirm is exactly what it is for.
 *
 * ## Escape is intercepted, and that is the point
 *
 * `dismissOnEscape` is false while dirty. The shell's own Esc would close and
 * silently discard local edits; instead Esc routes through the same
 * confirmation as the close button, so there is one way to lose work and it
 * asks first.
 */
export function ProposalReviewDialog(
  props: ProposalReviewDialogProps,
): ReactElement | null {
  const {
    open,
    onClose,
    title,
    description,
    service,
    riskLabel,
    summary,
    items,
    form,
    validationNote,
    dirty,
    state,
    stateMessage,
    canDecide,
    onDiscard,
    onReject,
    onApprove,
    approveLabel,
    approveDisabled = false,
    approvalConfirmation,
    discardConfirm,
  } = props;
  const titleId = useId();
  const descriptionId = useId();
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // Applying is deliberately NOT read-only-by-authority: the dialog stays open
  // and inert while the write is in flight, then shows the outcome.
  const inFlight = state === "applying";
  const readOnly = !canDecide || state !== "editing";
  const approvalRevision =
    open && !readOnly && !approveDisabled
      ? (approvalConfirmation?.revisionKey ?? null)
      : null;
  const [approvalPrompt, setApprovalPrompt] = useState({
    revision: approvalRevision,
    requested: false,
  });
  // Adjust the local prompt before rendering changed props, so a confirmation
  // cannot survive a new draft, lost authority, or closing and reopening.
  if (approvalPrompt.revision !== approvalRevision) {
    setApprovalPrompt({ revision: approvalRevision, requested: false });
  }
  const confirmingApproval =
    approvalRevision !== null &&
    approvalPrompt.revision === approvalRevision &&
    approvalPrompt.requested;

  /** Prevent in-flight dismissal and confirm before discarding an authorized draft. */
  const requestClose = (): void => {
    if (inFlight) return;
    // A clean dialog closes immediately; only unsaved work earns a prompt.
    if (dirty && canDecide) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  };

  /* No early return: ModalShell owns the closing frame, and a `return null`
     in front of it makes the exit unreachable (ADR-0091). */
  return (
    <>
      <ModalShell
        open={open}
        onClose={requestClose}
        labelledBy={titleId}
        describedBy={description ? descriptionId : undefined}
        dismissOnEscape={!dirty || !canDecide}
        dismissOnOutsidePress={false}
        containerClassName="flex items-center justify-center p-0 sm:p-6"
        panelClassName={cn(
          "flex flex-col bg-pb-card shadow-pbds-overlay",
          // Full-screen sheet below `sm`; a large centred panel above it.
          "h-dvh w-screen rounded-none",
          "sm:h-[min(50rem,92vh)] sm:w-[96vw] sm:max-w-[76rem] sm:rounded-xl",
        )}
      >
        <header className="flex flex-col gap-3 border-b border-pb-border px-6 py-5 sm:flex-row sm:items-start sm:gap-4">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="text-heading-2 font-semibold text-pb-text"
            >
              {title}
            </h2>
            {description ? (
              <p
                id={descriptionId}
                className="mt-0.5 text-caption text-pb-text-muted"
              >
                {description}
              </p>
            ) : null}
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0">
            {dirty && canDecide ? (
              <span className="inline-flex items-center gap-1.5 text-caption text-pb-text-muted">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 rounded-full bg-pb-amber"
                />
                Unsaved edits
              </span>
            ) : null}
            {service !== undefined ? (
              <Badge>{serviceLabel(service)}</Badge>
            ) : null}
            {riskLabel ? (
              <Badge
                variant="outline"
                className="border-pb-amber/40 text-pb-amber-text"
              >
                <ShieldCheck className="h-3 w-3" aria-hidden />
                {riskLabel}
              </Badge>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Close"
              onClick={requestClose}
              disabled={inFlight}
              className="ms-auto"
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div className="flex items-center gap-4 rounded-lg border border-pb-border bg-pb-card-elevated px-4 py-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pb-amber-soft">
              <ListChecks className="h-5 w-5 text-pb-amber-text" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-body font-semibold text-pb-text">
                {summary.headline}
              </p>
              {summary.detail ? (
                <p className="text-caption text-pb-text-muted">
                  {summary.detail}
                </p>
              ) : null}
            </div>
          </div>

          {/* Non-editing states announce themselves before the body, so the
              outcome is read first rather than found under a form. */}
          {stateMessage && state !== "editing" ? (
            <p
              role="status"
              className={cn(
                "mt-3 rounded-md border px-3 py-2 text-dense-1",
                state === "failed" || state === "stale"
                  ? "border-pb-red/30 bg-pb-red-soft text-pb-red-text"
                  : "border-pb-border bg-pb-shell/60 text-pb-text-secondary",
              )}
            >
              {stateMessage}
            </p>
          ) : null}

          <div
            className={cn(
              "mt-5 grid gap-5",
              items ? "lg:grid-cols-[minmax(0,25rem)_minmax(0,1fr)]" : "",
            )}
          >
            {items ? (
              <div className="lg:max-h-[46vh] lg:min-h-0">
                <ProposalReviewItemList model={items} readOnly={readOnly} />
              </div>
            ) : null}
            <div
              className={cn(
                items ? "lg:border-s lg:border-pb-border lg:ps-5" : "",
              )}
            >
              {form.heading ? (
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="min-w-0 truncate text-label font-semibold text-pb-text">
                    {form.heading}
                  </h3>
                  {form.changedLabel ? (
                    <span className="inline-flex shrink-0 items-center gap-1.5 text-dense-2 text-pb-text-muted">
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full bg-pb-amber"
                      />
                      {form.changedLabel}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <ProposalReviewForm model={form} readOnly={readOnly} />
              <ProposalReviewFormFooter model={form} readOnly={readOnly} />
            </div>
          </div>
        </div>

        <footer className="flex flex-col items-stretch gap-3 border-t border-pb-border px-6 py-4 sm:flex-row sm:flex-wrap sm:items-center">
          {validationNote ? (
            <p className="inline-flex min-w-0 flex-1 items-center gap-2 text-caption text-pb-text-muted">
              <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0">{validationNote}</span>
            </p>
          ) : (
            <span className="flex-1" />
          )}
          <div className="grid w-full shrink-0 grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
            {canDecide && onDiscard ? (
              // Reverts the draft and LEAVES THE DIALOG OPEN. Deliberately not
              // labelled from `discardConfirm` — that copy belongs to the
              // confirmation's own action, which additionally closes, and two
              // buttons doing different things must not share a name.
              <Button
                variant="secondary"
                onClick={onDiscard}
                disabled={!dirty || inFlight}
              >
                Discard edits
              </Button>
            ) : null}
            {canDecide && onReject ? (
              <Button
                variant="secondary"
                onClick={onReject}
                disabled={state !== "editing"}
              >
                Reject
              </Button>
            ) : null}
            {canDecide ? (
              <Button
                variant="primary"
                onClick={() => {
                  if (approvalConfirmation) {
                    setApprovalPrompt({
                      revision: approvalRevision,
                      requested: true,
                    });
                  } else {
                    onApprove();
                  }
                }}
                disabled={state !== "editing" || approveDisabled}
                className="col-span-2 sm:col-auto"
              >
                {inFlight
                  ? "Applying…"
                  : (approveLabel ??
                    (dirty ? "Approve edited suggestion" : "Approve"))}
              </Button>
            ) : null}
          </div>
        </footer>
      </ModalShell>

      <ProposalApprovalConfirmation
        open={confirmingApproval}
        model={approvalConfirmation}
        onClose={() =>
          setApprovalPrompt({ revision: approvalRevision, requested: false })
        }
        onConfirm={() => {
          if (!confirmingApproval) return;
          setApprovalPrompt({ revision: approvalRevision, requested: false });
          onApprove();
        }}
      />

      {/* A destructive confirm is the shared ConfirmDialog (d2): the red
          chip, and the ghost Cancel that takes focus (AP-16). */}
      <ConfirmDialog
        open={confirmingDiscard}
        title={discardConfirm?.title ?? "Discard your edits?"}
        confirmLabel={discardConfirm?.confirmLabel ?? "Discard and close"}
        cancelLabel={discardConfirm?.cancelLabel ?? "Keep editing"}
        onClose={() => setConfirmingDiscard(false)}
        onConfirm={() => {
          setConfirmingDiscard(false);
          onDiscard?.();
          onClose();
        }}
      >
        {discardConfirm?.body ??
          "Your changes have not been submitted and will be lost."}
      </ConfirmDialog>
    </>
  );
}

/**
 * The service badge's words.
 *
 * Allowlisted here for the same reason activity copy is: the only other source
 * would be remote catalogue metadata, which is untrusted by contract.
 */
function serviceLabel(service: string | null | undefined): string {
  if (service === "jira") return "Jira";
  if (service === "confluence") return "Confluence";
  return "Connected service";
}
